import liveServerStatus from '../../liveServerStatus';

/**
 * Generate HTML email template for weekly report reviewed notifications
 * @param {Object} employeeData - Employee information
 * @param {Object} reviewData - Information about the review
 * @returns {string} - HTML email content
 */
export const generateReportReviewedEmailHTML = (employeeData, reviewData) => {
  // Add test environment indicator in the header if not in live environment
  const testEnvironmentBanner = !liveServerStatus ? `
    <div style="background-color: #ffeb3b; color: #333; text-align: center; padding: 5px; font-weight: bold; font-size: 12px;">
      ⚠️ TEST EMAIL - No action required ⚠️
    </div>
  ` : '';

  // Get first name for greeting
  const firstName = employeeData.name ? employeeData.name.split(' ')[0] : 'there';

  // Determine score color based on value
  let scoreColor = '#4caf50'; // Green for good score (>14)
  
  if (reviewData.score < 10) {
    scoreColor = '#f44336'; // Red for poor score
  } else if (reviewData.score < 14) {
    scoreColor = '#ff9800'; // Orange for average score
  }

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Weekly Report Reviewed</title>
      <style>
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 0;
          background-color: #f5f5f5;
        }
        .email-container {
          max-width: 600px;
          margin: 0 auto;
          background-color: #ffffff;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
        }
        .email-header {
          background-color: #424242;
          color: white;
          padding: 20px;
          text-align: center;
        }
        .email-header h1 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
        }
        .email-body {
          padding: 30px;
        }
        .greeting {
          font-size: 18px;
          font-weight: 500;
          margin-bottom: 20px;
          color: #212121;
        }
        .review-box {
          background-color: #f5f5f5;
          border-radius: 6px;
          padding: 25px;
          margin-bottom: 25px;
          border: 1px solid #e0e0e0;
        }
        .week-period {
          font-size: 16px;
          font-weight: 500;
          color: #616161;
          margin-bottom: 15px;
        }
        .score-display {
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 20px 0;
        }
        .score-circle {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background-color: ${scoreColor};
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          font-weight: 700;
          margin-right: 20px;
        }
        .score-details {
          text-align: left;
        }
        .score-title {
          font-size: 16px;
          font-weight: 600;
          color: #424242;
          margin-bottom: 4px;
        }
        .score-scale {
          font-size: 14px;
          color: #757575;
        }
        .review-feedback {
          margin-top: 15px;
          font-style: italic;
          color: #424242;
          border-left: 3px solid #9e9e9e;
          padding-left: 15px;
        }
        .divider {
          height: 1px;
          background-color: #e0e0e0;
          margin: 25px 0;
        }
        .cta-button {
          display: inline-block;
          background-color: #4a6da7;
          color: white;
          text-decoration: none;
          padding: 12px 25px;
          border-radius: 4px;
          font-weight: 500;
          margin-top: 5px;
          text-align: center;
        }
        .message {
          margin-bottom: 25px;
          color: #424242;
        }
        .email-footer {
          background-color: #212121;
          padding: 20px;
          text-align: center;
          color: #f5f5f5;
          font-size: 12px;
        }
        .company-name {
          font-weight: 600;
          color: #f5f5f5;
          margin-bottom: 8px;
        }
      </style>
    </head>
    <body>
      ${testEnvironmentBanner}
      <div class="email-container">
        <div class="email-header">
          <h1>Weekly Report Reviewed</h1>
        </div>
        
        <div class="email-body">
          <div class="greeting">
            Hello ${firstName},
          </div>
          
          <div class="message">
            Your weekly progress report has been reviewed by your manager. Please find your performance evaluation below.
          </div>
          
          <div class="review-box">
            <div class="week-period">Week of ${reviewData.weekStart} - ${reviewData.weekEnd}</div>
            
            <div class="score-display">
              <div class="score-circle">${reviewData.score}</div>
              <div class="score-details">
                <div class="score-title">Performance Score</div>
                <div class="score-scale">Scale: 1-20</div>
              </div>
            </div>
            
            ${reviewData.feedback ? `
            <div class="review-feedback">
              "${reviewData.feedback}"
            </div>
            ` : ''}
          </div>
          
          <a href="${process.env.REACT_APP_URL || 'https://revamped-ppc.myelinz.com'}/performance-review" class="cta-button">View Full Review</a>
          
          <div class="divider"></div>
          
          <div class="message">
            Please review your manager's feedback and consider the areas highlighted for improvement. If you have any questions about your evaluation, please contact your manager directly.
          </div>
        </div>
        
        <div class="email-footer">
          <div class="company-name">MyelinZ PPC Portal</div>
          <p>This is an automated message. Please do not reply directly to this email.</p>
          <p>© ${new Date().getFullYear()} MyelinZ. All rights reserved.</p>
          ${!liveServerStatus ? '<p><strong>TEST ENVIRONMENT - No action required</strong></p>' : ''}
        </div>
      </div>
    </body>
    </html>
  `;
};

export default generateReportReviewedEmailHTML; 